import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { toast } from "sonner";

// Per-user lock preferences stored on user_preferences — follows the
// user across devices. Single React Query cache entry on
// /api/me/preferences so both this hook and UnifiedHome's section-state
// consumer share state.

export type LockPrefs = {
  enabled: boolean;
  timeoutMinutes: number;
};

export const DEFAULT_LOCK_PREFS: LockPrefs = {
  enabled: true,
  timeoutMinutes: 15,
};

export const ALLOWED_TIMEOUTS_MINUTES: readonly number[] = [
  1, 5, 10, 15, 30, 60, 120,
] as const;

type Prefs = {
  unifiedHomeSectionStates?: Record<string, boolean>;
  lockEnabled: boolean;
  lockTimeoutMinutes: number;
};

export function useLockPrefs(): {
  prefs: LockPrefs;
  isLoading: boolean;
  setEnabled: (v: boolean) => void;
  setTimeoutMinutes: (v: number) => void;
} {
  const { data: user } = useSession();
  const qc = useQueryClient();

  const q = useQuery<Prefs>({
    queryKey: ["/api/me/preferences"],
    queryFn: async () => {
      const r = await fetch("/api/me/preferences", { credentials: "include" });
      if (!r.ok) {
        return {
          lockEnabled: DEFAULT_LOCK_PREFS.enabled,
          lockTimeoutMinutes: DEFAULT_LOCK_PREFS.timeoutMinutes,
        };
      }
      return (await r.json()).preferences as Prefs;
    },
    enabled: Boolean(user),
  });

  const patch = useMutation({
    mutationFn: async (p: Partial<Pick<Prefs, "lockEnabled" | "lockTimeoutMinutes">>) => {
      const r = await fetch("/api/me/preferences", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `PATCH failed (${r.status})`);
      }
    },
    // Optimistic: flip the cache immediately so the Settings toggle
    // feels responsive; rollback on error.
    onMutate: async (p) => {
      await qc.cancelQueries({ queryKey: ["/api/me/preferences"] });
      const prev = qc.getQueryData<Prefs>(["/api/me/preferences"]);
      qc.setQueryData<Prefs>(["/api/me/preferences"], (old) => ({
        unifiedHomeSectionStates: old?.unifiedHomeSectionStates ?? {},
        lockEnabled:
          p.lockEnabled ?? old?.lockEnabled ?? DEFAULT_LOCK_PREFS.enabled,
        lockTimeoutMinutes:
          p.lockTimeoutMinutes ??
          old?.lockTimeoutMinutes ??
          DEFAULT_LOCK_PREFS.timeoutMinutes,
      }));
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      toast.error(err.message);
      if (ctx?.prev) qc.setQueryData(["/api/me/preferences"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["/api/me/preferences"] }),
  });

  // Pre-session or pre-fetch: return the safe defaults (enabled, 15 min)
  // so auto-lock doesn't get disabled by accident during load.
  const prefs: LockPrefs = q.data
    ? {
        enabled: q.data.lockEnabled ?? DEFAULT_LOCK_PREFS.enabled,
        timeoutMinutes:
          q.data.lockTimeoutMinutes ?? DEFAULT_LOCK_PREFS.timeoutMinutes,
      }
    : DEFAULT_LOCK_PREFS;

  return {
    prefs,
    isLoading: q.isLoading,
    setEnabled: (v) => patch.mutate({ lockEnabled: v }),
    setTimeoutMinutes: (v) => patch.mutate({ lockTimeoutMinutes: v }),
  };
}
